import * as React from "react"
import { useQuery } from "react-query"
import { Box, Flex, Heading, HStack, IconButton, Link, Select, Text } from "@chakra-ui/react"
import { Link as RLink, useNavigate } from "react-router-dom"

import { useOrgs } from "../components/providers/OrgProvider"
import { Column, Table } from "../components/Table"
import { SearchBar } from "../components/SearchBar"
import { TABLE_PAGE_SIZE, TOPBAR_HEIGHT } from "../lib/config"
import { fetcher } from "../lib/fetcher"
import { Order } from "../lib/types/shared"
import { TrackerEntity } from "../lib/types/tracker"
import { useDocumentTitle } from "../lib/hooks/useDocumentTitle"
import { TRACKER_STATE_OPTIONS } from "../lib/static/tracker"
import { getBikeUuidFromUrl } from "../lib/helpers/utils"
import dayjs from "dayjs"
import { FiExternalLink } from "react-icons/fi"

interface TrackersResponse {
  count: number
  results: TrackerEntity[]
}

export const Trackers = () => {
  useDocumentTitle("Trackers")

  const { currentOrgs } = useOrgs()
  const navigate = useNavigate()
  const [search, setSearch] = React.useState<string>("")

  const [order, setOrder] = React.useState<Order>({
    order: "-",
    orderBy: "created",
  })
  const [page, setPage] = React.useState<number>(1)
  const [filterState, setFilterState] = React.useState<string>("")

  const currentOrgsUuids = currentOrgs.map(({ uuid }) => uuid)

  const trackersUrl = `locks/?page_size=${TABLE_PAGE_SIZE}&ordering=${
    order.order + order.orderBy
  }&organizations=${currentOrgsUuids.join()}&query=${search}&type=tracker${
    filterState !== "" ? `&state=${filterState}` : ""
  }`

  const getTrackers = React.useCallback(
    (currentPage: number) => fetcher(trackersUrl + "&page=" + currentPage),
    [trackersUrl],
  )

  const { isLoading, data } = useQuery<TrackersResponse, [string, number]>(
    [trackersUrl, page],
    () => getTrackers(page),
    { keepPreviousData: true },
  )
  const trackers = data?.results || []

  const handleSetState = (state: string) => {
    setFilterState(state)
    setPage(1)
  }
  const handleSetSearch = (search: string) => {
    setSearch(search)
    setPage(1)
  }
  const handleSetOrder = (order: Order) => {
    setOrder(order)
    setPage(1)
  }

  return (
    <Box>
      <Flex px={6} justify="space-between" align="center" h={TOPBAR_HEIGHT}>
        <Heading as="h3" color="gray.700">
          Trackers
        </Heading>
        <Flex justify="flex-end" align="center">
          <HStack spacing={4}>
            <HStack spacing={2}>
              <Text>State:</Text>
              <Select w="200px" placeholder="Any" onChange={(e) => handleSetState(e.target.value)}>
                {TRACKER_STATE_OPTIONS.map((option, index) => (
                  <option key={index} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </HStack>

            <SearchBar onSearchSubmit={handleSetSearch} search={search} />
          </HStack>
        </Flex>
      </Flex>
      <Box px={6} pb={6} overflowY="auto" maxHeight={`calc(100vh - ${TOPBAR_HEIGHT})`}>
        <Table
          isLoading={isLoading}
          data={trackers}
          count={data?.count || 0}
          noData="No trackers"
          page={page}
          pageSize={TABLE_PAGE_SIZE}
          sort={order}
          onSort={handleSetOrder}
          setPage={setPage}
        >
          <Column<TrackerEntity>
            sortKey="bleid"
            header="ID"
            row={(tracker) => (
              <Link
                as={RLink}
                to={`/trackers/${tracker.uuid}`}
                fontSize="0.8rem"
                fontWeight="600"
                whiteSpace="nowrap"
              >
                {tracker.bleid}
              </Link>
            )}
          />
          <Column<TrackerEntity>
            header="Asset"
            row={(tracker) => (
              <Flex align="center">
                <Box>
                  {tracker.bicycle ? (
                    <Flex align="center" justify="space-between">
                      <Link
                        as={RLink}
                        to={`/assets/${getBikeUuidFromUrl(tracker.bicycle)}`}
                        fontSize="0.8rem"
                        fontWeight="600"
                        whiteSpace="nowrap"
                      >
                        View Asset
                      </Link>
                      <IconButton
                        aria-label="go to asset"
                        icon={<FiExternalLink />}
                        boxSize="30px"
                        cursor="pointer"
                        minW="30px"
                        p={2}
                        variant="ghost"
                        onClick={() => navigate(`/assets/${getBikeUuidFromUrl(tracker.bicycle)}`)}
                      />
                    </Flex>
                  ) : null}
                </Box>
              </Flex>
            )}
          />
          <Column<TrackerEntity>
            // sortKey="firmware_version"
            header="Fw version"
            row={(tracker) => tracker.firmware_version}
          />
          <Column<TrackerEntity>
            sortKey="state"
            header="State"
            row={(tracker) => tracker.state}
            maxW="150px"
          />
          <Column<TrackerEntity>
            sortKey="state_of_charge"
            header="Battery"
            row={(tracker) => `${Math.round(tracker.state_of_charge)} %`}
            maxW="80px"
          />
          <Column<TrackerEntity>
            sortKey="latest_gps_timestamp"
            header="Updated"
            maxW="150px"
            row={(tracker) => (
              <Flex align="center">
                <Box>
                  {tracker.latest_gps_timestamp ? (
                    <Text color="gray.700" fontSize="0.8rem">
                      {dayjs(tracker.latest_gps_timestamp).fromNow()}
                    </Text>
                  ) : null}
                </Box>
              </Flex>
            )}
          />
        </Table>
      </Box>
    </Box>
  )
}
